import { DOCS_YEARS_ORDER, OPTIONS, rowSourceText, type IntakeAnswers } from '@upm/domain';
import { ChoiceGroup, TextField } from '../../ui';
import { useIntake } from '../IntakeContext';
import { toggleMulti } from '../logic';
import { FormCard, opts, StepHeader } from './common';

type DocScope = NonNullable<IntakeAnswers['docsScope']>[number];

export function StepDocs() {
  const { answers: a, setAnswers, tools, catalogLabels } = useIntake();
  const set = (p: Partial<IntakeAnswers>) => setAnswers(4, p);
  const src = rowSourceText(tools, ['storage', 'portal'], catalogLabels);
  const docSources = src === 'Not answered' ? 'nothing selected yet' : src;
  return (
    <div className="flex flex-col gap-6">
      <StepHeader step={4} title="Documents">We want to understand which documents you’d like to bring over to Upstream Portal.</StepHeader>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3.5">
        <div className="rounded-card border border-line bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-[0.06em] text-brand">Comes with you</div>
          <div className="mb-1.5 mt-0.5 text-[17px] font-bold">Final documents</div>
          <div className="text-sm text-ink-2">Finished work you or your clients may need again: filed returns, signed engagement letters and the documents clients sent you for this tax year.</div>
        </div>
        <div className="rounded-card border border-line bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-[0.06em] text-muted">Stays where it is</div>
          <div className="mb-1.5 mt-0.5 text-[17px] font-bold">Work papers</div>
          <div className="text-sm text-ink-2">Files you edit to do the job: working spreadsheets, calculations, drafts and notes. They stay in your current storage, which remains your working space.</div>
        </div>
      </div>
      <FormCard>
      <ChoiceGroup
        legend="Which final documents should be in Upstream Portal on day one?"
        hint="Pick all that apply."
        options={opts(OPTIONS.docsScope)}
        value={a.docsScope ?? []}
        onToggle={(v) => set({ docsScope: toggleMulti<DocScope>(a.docsScope as DocScope[] | undefined, v as DocScope, 'none') as IntakeAnswers['docsScope'] })}
      />
      <ChoiceGroup legend="How many years back?" options={DOCS_YEARS_ORDER.map((v) => ({ value: v, label: OPTIONS.docsYears[v] }))} value={a.docsYears} onToggle={(v) => set({ docsYears: v as IntakeAnswers['docsYears'] })} />
      <ChoiceGroup legend="Roughly how much is that?" options={opts(OPTIONS.docsSize)} value={a.docsSize} onToggle={(v) => set({ docsSize: v as IntakeAnswers['docsSize'] })} />
      <div className="border-t border-line-soft pt-5">
        <div className="text-[15px] font-semibold">Where they live today</div>
        <div className="mb-2 mt-0.5 text-sm text-ink-2">From your tools answers: {docSources}</div>
        <TextField
          id="docWhere"
          small
          label="Anywhere else?"
          optional
          placeholder="For example: a filing cabinet of signed engagement letters"
          value={a.docsOtherLocation ?? ''}
          onChange={(e) => set({ docsOtherLocation: e.target.value })}
        />
      </div>
      </FormCard>
    </div>
  );
}
